import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { GitForkIcon } from '@phosphor-icons/react'
import moment from 'moment'
import { type ReactNode } from 'react'
import { EmailMessageCard } from 'app/components/contacts/inbox/EmailMessageCard'
import { AISparkle } from 'app/components/Icon/AIBadgeIcon'
import { LazyUserCell } from 'app/components/LazyUserCell'
import PhoneNumber from 'app/components/PhoneNumber'
import { Tooltip } from 'app/components/Tooltip'
import type { ContactJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import { getMessageDelivery } from 'app/lib/messageDelivery'
import { OPT_OUT_LINE } from 'app/lib/smsOptOut'

function formatMessageTimestamp(value: MessageJson[`createdDate`]): string {
  if (!value) return ``
  const m = moment.utc(value).local()
  if (!m.isValid()) return ``
  const startOfToday = moment().startOf(`day`)
  const startOfMessage = m.clone().startOf(`day`)
  const daysAgo = startOfMessage.diff(startOfToday, `days`)
  const time = m.format(`h:mm A`)
  if (daysAgo === 0) return time
  if (daysAgo === -1) return `Yesterday ${time}`
  if (daysAgo > -7) return `${m.format(`ddd`)} ${time}`
  if (m.year() === moment().year()) return `${m.format(`MMM D`)}, ${time}`
  return `${m.format(`MMM D, YYYY`)}, ${time}`
}

type MessageType = `ai` | `outgoing` | `incoming` | `error`

const EMAIL_STATUS_LABELS = { pending: `Sending`, sent: `Sent`, delivered: `Delivered` } as const

type Props = {
  contact?: ContactJson
  message: MessageJson
  isSuperUser?: boolean
  /** Super-user debug metadata chips rendered inline next to the timestamp. */
  metaSlot?: ReactNode
  /**
   * Stretch the bubble to fill its container instead of shrinking to content.
   * Used so a debug turn's tool bubble matches the agent message width when the
   * two share a width-capped column.
   */
  fullWidth?: boolean
  /**
   * iMessage-style receipt: the thread owner (ContactInbox) sets this on the
   * latest outbound message only, and only when the carrier confirmed delivery.
   */
  showDelivered?: boolean
  /**
   * Render CallRail's automatic first-message opt-out append. CallRail adds
   * `Reply STOP to opt out.` to the first text of a conversation and never
   * reports it back, so `message.text` is our pre-append body. ContactInbox
   * owns the decision (see `optOutAppendMessageId`); this only renders it.
   */
  showOptOutAppend?: boolean
}

function getMessageType(message: MessageJson, _isSuperUser?: boolean): MessageType {
  if (!message.text && message.channel !== `email`) return `error`
  if (message.isAgent) return `ai`
  if (message.sentByUserId) return `outgoing`
  return `incoming`
}

export function MessageCard({
  contact,
  message,
  isSuperUser,
  metaSlot,
  fullWidth,
  showDelivered,
  showOptOutAppend,
}: Props) {
  const type = getMessageType(message, isSuperUser)
  const outgoing = type === `outgoing` || type === `ai`
  // Both the workflow engine and the AI agent stamp `aiWorkflowId` (the agent keeps the id of the
  // workflow that briefed it), so only a run id tells a workflow send from an agent reply.
  const workflowName = message.isAgent && message.workflowRunId ? message.aiWorkflowName || `Workflow` : undefined
  // Carrier-level failure (TRK-5461). Orthogonal to bubble type — AI/workflow
  // sends fail the same way manual ones do.
  const delivery = getMessageDelivery(message)
  const failedDelivery = outgoing && delivery.status === `failed`
  const deliveredLabel =
    message.channel === `email` && delivery.status && delivery.status !== `failed`
      ? EMAIL_STATUS_LABELS[delivery.status]
      : showDelivered && !failedDelivery
        ? `Delivered`
        : undefined

  return (
    <div className="group flex w-full flex-col gap-y-1 pt-1" data-type={type}>
      <div className="flex justify-end gap-x-1.5 group-data-[type=incoming]:justify-start">
        {failedDelivery && (
          <Tooltip
            message={`Not delivered — ${delivery.failureReason}`}
            placement="top"
            className="flex shrink-0 self-center"
          >
            <ExclamationTriangleIcon
              role="img"
              aria-label={`Not delivered — ${delivery.failureReason}`}
              className="h-5 w-5 text-red-500"
            />
          </Tooltip>
        )}
        {message.channel === `email` ? (
          <EmailMessageCard message={message} />
        ) : (
          <div
            className={[
              // whitespace-pre-wrap keeps the line breaks the sender typed; break-words
              // stops a long unbroken URL or number from blowing out the bubble width.
              `relative whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-body-sm`,
              fullWidth ? `w-full` : `max-w-[75%]`,
              // Asymmetric tail corner — flatter on the side the bubble originates from
              `data-[type=incoming]:rounded-bl-md`,
              `data-[type=outgoing]:rounded-br-md`,
              `data-[type=ai]:rounded-br-md`,
              `data-[type=error]:rounded-br-md`,
              `data-[type=outgoing]:bg-blue-600 data-[type=outgoing]:text-on-color`,
              `data-[type=incoming]:bg-surface-muted data-[type=incoming]:text-strong`,
              `data-[type=ai]:text-strong data-[type=ai]:ring-1 data-[type=ai]:ring-blue-200`,
              `data-[type=error]:bg-red-50 data-[type=error]:text-red-700`,
            ].join(` `)}
            data-type={type}
            style={
              type === `ai`
                ? {
                    background: `linear-gradient(135deg, rgba(103, 195, 255, 0.12) 0%, rgba(123, 80, 255, 0.12) 100%)`,
                  }
                : undefined
            }
          >
            {type === `ai` && (
              <span
                aria-hidden
                className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-surface ring-1 ring-blue-200"
              >
                {workflowName ? (
                  <GitForkIcon size={12} weight="bold" className="text-violet-600" />
                ) : (
                  <AISparkle className="h-3 w-3" gradientId={`ai-bubble-${message.id ?? `unk`}`} />
                )}
              </span>
            )}
            {message.text ?? `Uh oh. There was a problem loading this message.`}
            {showOptOutAppend && (
              <Tooltip message="Added automatically by CallRail" placement="top">
                {/* role="note" so the label is announced. A bare span is role
                  generic, where author-supplied names are suppressed. */}
                <span role="note" aria-label="Added automatically by CallRail">{`\n${OPT_OUT_LINE}`}</span>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 px-1 text-label-xs text-subtle group-data-[type=incoming]:justify-start group-data-[type=outgoing]:justify-end group-data-[type=ai]:justify-end">
        {deliveredLabel && (
          <>
            <span className="font-medium text-subtle">{deliveredLabel}</span>
            <span aria-hidden>·</span>
          </>
        )}
        {metaSlot && (
          <>
            {metaSlot}
            <span aria-hidden>·</span>
          </>
        )}
        <span>{formatMessageTimestamp(message.createdDate)}</span>
        <span>·</span>
        <span>
          {workflowName ? (
            <span className="inline-flex items-center gap-x-1" title="Sent automatically by a workflow">
              <GitForkIcon size={10} weight="bold" className="text-violet-600" />
              <span className="font-medium text-violet-700">{workflowName}</span>
            </span>
          ) : message.isAgent ? (
            <span className="inline-flex items-center gap-x-1">
              <AISparkle className="h-2.5 w-2.5" gradientId="ai-msg-sparkle" />
              <span className="font-medium text-blue-700">AI Agent</span>
            </span>
          ) : message.sentByUserId === `external` ? (
            <span title="Sent outside Trackable, such as an automatic reply or a text from the CallRail app">
              External
            </span>
          ) : message.sentByUserId ? (
            <LazyUserCell userId={message.sentByUserId} />
          ) : (
            <PhoneNumber number={message.from} />
          )}
        </span>
      </div>
    </div>
  )
}
